import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useMessageInputContext, useChannelStateContext } from 'stream-chat-react';
import { useDropzone } from 'react-dropzone';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import { EmojiPicker } from './EmojiPicker';
import type { StreamChatType } from '../types';
import './ModernMessageInput.scss';

type FormatType = 'bold' | 'italic' | 'strikethrough';

interface FormatCommand {
  markdown: (text: string) => string;
  shortcut: string;
}

const formatCommands: Record<FormatType, FormatCommand> = {
  bold: {
    markdown: (text) => `**${text}**`,
    shortcut: 'Ctrl+B',
  },
  italic: {
    markdown: (text) => `*${text}*`,
    shortcut: 'Ctrl+I',
  },
  strikethrough: {
    markdown: (text) => `~~${text}~~`,
    shortcut: 'Ctrl+Shift+S',
  },
};

export const ModernMessageInput: React.FC = () => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const { handleSubmit, text, setText, uploadNewFiles, maxFilesLeft, numberOfUploads } = useMessageInputContext<StreamChatType>();
  const { acceptedFiles = [], multipleUploads } = useChannelStateContext<StreamChatType>();

  // File upload dropzone
  // Convert acceptedFiles array to the format expected by react-dropzone v14+
  const acceptObject = acceptedFiles.length > 0 
    ? acceptedFiles.reduce((acc, curr) => {
        acc[curr] = [];
        return acc;
      }, {} as Record<string, string[]>)
    : undefined;

  const { getRootProps, getInputProps, open: openFilePicker } = useDropzone({
    accept: acceptObject,
    multiple: multipleUploads,
    noClick: true,
    noKeyboard: true,
    onDrop: (acceptedFiles) => {
      uploadNewFiles(acceptedFiles);
    },
  });

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    const newHeight = Math.min(textarea.scrollHeight, 200); // Max 200px
    textarea.style.height = `${newHeight}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [text, adjustHeight]);

  // Check for text selection (for mobile toolbar repositioning)
  const checkTextSelection = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const hasSelectedText = start !== end;

    setHasSelection(hasSelectedText);
  }, []);

  // Listen for selection changes on mobile
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Only add these listeners on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    const handleSelectionChange = () => {
      checkTextSelection();
    };

    textarea.addEventListener('select', handleSelectionChange);
    textarea.addEventListener('selectstart', handleSelectionChange);
    textarea.addEventListener('mouseup', handleSelectionChange);
    textarea.addEventListener('touchend', handleSelectionChange);
    textarea.addEventListener('keyup', handleSelectionChange);

    return () => {
      textarea.removeEventListener('select', handleSelectionChange);
      textarea.removeEventListener('selectstart', handleSelectionChange);
      textarea.removeEventListener('mouseup', handleSelectionChange);
      textarea.removeEventListener('touchend', handleSelectionChange);
      textarea.removeEventListener('keyup', handleSelectionChange);
    };
  }, [checkTextSelection]);

  // Handle keyboard appearing on mobile (especially Chrome)
  useEffect(() => {
    const textarea = textareaRef.current;
    const container = containerRef.current;
    if (!textarea || !container) return;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) return;

    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    
    const handleFocus = () => {
      // Small delay to allow keyboard to start appearing
      setTimeout(() => {
        // Scroll the input into view
        container.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end',
          inline: 'nearest' 
        });
      }, 300);
    };

    const handleResize = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      
      // If viewport height decreased significantly, keyboard is likely visible
      if (initialViewportHeight - currentHeight > 150) {
        // Scroll input into view when keyboard appears
        setTimeout(() => {
          container.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'end',
            inline: 'nearest' 
          });
        }, 100);
      }
      
      initialViewportHeight = currentHeight;
    };

    // Listen to focus event
    textarea.addEventListener('focus', handleFocus);
    
    // Listen to viewport resize (keyboard appearing/disappearing)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      textarea.removeEventListener('focus', handleFocus);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  // Handle text change
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

    // Desktop: Enter sends, Shift+Enter for new line
    // Mobile: Enter for new line, send button to send
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (e.key === 'Enter') {
      if (!isMobile && !e.shiftKey) {
        // Desktop: Enter sends message
        e.preventDefault();
        if (text.trim()) {
          handleSubmit(e as any);
        }
        return;
      }
      // Mobile or Shift+Enter: Allow new line (default behavior)
    }

    // Formatting shortcuts
    if (ctrlKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          applyFormat('bold');
          break;
        case 'i':
          e.preventDefault();
          applyFormat('italic');
          break;
        case 's':
          if (e.shiftKey) {
            e.preventDefault();
            applyFormat('strikethrough');
          }
          break;
      }
    }
  };

  // Apply formatting to selected text
  const applyFormat = (format: FormatType) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = text.substring(start, end);

    if (selectedText) {
      // Format selected text
      const formattedText = formatCommands[format].markdown(selectedText);
      const newText = text.substring(0, start) + formattedText + text.substring(end);
      setText(newText);

      // Restore selection after state update
      setTimeout(() => {
        const offset = format === 'bold' || format === 'strikethrough' ? 2 : 1;
        textarea.focus();
        textarea.setSelectionRange(start + offset, start + offset + selectedText.length);
      }, 0);
    } else {
      // No selection - insert formatting markers at cursor
      const markers = formatCommands[format].markdown('text');
      const beforeMarker = markers.split('text')[0];
      const afterMarker = markers.split('text')[1];
      const newText = text.substring(0, start) + beforeMarker + afterMarker + text.substring(end);
      setText(newText);

      // Place cursor between markers
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + beforeMarker.length, start + beforeMarker.length);
      }, 0);
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`modern-message-input ${hasSelection ? 'modern-message-input--toolbar-shifted' : ''}`} 
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      
      {/* Formatting Toolbar */}
      <div 
        ref={toolbarRef}
        className={`modern-message-input__toolbar ${hasSelection ? 'modern-message-input__toolbar--shifted' : ''}`}
      >
        {/* Emoji Picker - First */}
        <div className="toolbar-emoji-wrapper">
          <EmojiPicker />
        </div>

        {/* Divider */}
        <div className="toolbar-divider" />

        {/* Formatting Buttons */}
        <button
          type="button"
          className="toolbar-button"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent focus loss on mobile
            applyFormat('bold');
          }}
          title="Bold (Ctrl+B)"
        >
          <FormatBoldIcon />
        </button>
        <button
          type="button"
          className="toolbar-button"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent focus loss on mobile
            applyFormat('italic');
          }}
          title="Italic (Ctrl+I)"
        >
          <FormatItalicIcon />
        </button>
        <button
          type="button"
          className="toolbar-button"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent focus loss on mobile
            applyFormat('strikethrough');
          }}
          title="Strikethrough (Ctrl+Shift+S)"
        >
          <StrikethroughSIcon />
        </button>

        {/* Divider */}
        <div className="toolbar-divider" />

        {/* Attachment Button - Last */}
        <button
          type="button"
          className="toolbar-button toolbar-button--attachment"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent focus loss on mobile
            openFilePicker();
          }}
          title="Attach files"
          disabled={maxFilesLeft === 0}
        >
          <AttachFileIcon />
        </button>

        {/* File count indicator */}
        {numberOfUploads > 0 && (
          <span className="toolbar-file-count">{numberOfUploads}</span>
        )}
      </div>

      {/* Input Area */}
      <div className="modern-message-input__input-area">
        <textarea
          ref={textareaRef}
          className="modern-message-input__textarea"
          placeholder="Type a message..."
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button
          type="button"
          className="modern-message-input__send-button"
          onMouseDown={(e) => {
            e.preventDefault(); // Prevent focus loss before sending
            if (text.trim()) {
              handleSubmit(e as any);
            }
          }}
          disabled={!text.trim()}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
};

